import * as _ from 'lodash';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { catchError, retryWhen } from 'rxjs/operators';
import { distinctUntilChanged } from 'rxjs/operators/distinctUntilChanged';
import { filter } from 'rxjs/operators/filter';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { map } from 'rxjs/operators/map';
import { of } from 'rxjs/observable/of';

import { ApplicationActions } from '../../actions/application.actions';
import { ApplicationProxyService } from './applicationproxy.service';
import { FlowManagerService } from './flowmanager.service';
import { QuestionnaireTransformer } from '../helpers/questionnaire.transformer';
import { StrategyType } from '../models/Enum/StrategyType';
import { TKARiskTolerance as RiskTolerance } from '../models/Enum/TKARiskTolerance';
import { TimeHorizonsBase as TimeHorizon } from '../models/Base/TimeHorizons';
import { genericRetryStrategy } from '../helpers/genericRetryStrategy';

import {
  ModelSelectionRequest,
  MappedModelSelectionResponse,
  PortfolioKey,
  Strategy
} from '../models/Recommendation/RecommendationInterfaces';
@Injectable()
export class QuestionnaireService {
  /* private */ _strategies$ = new BehaviorSubject([]); // don't access this one
  /* private */ dataStore: { strategies: Strategy[] } = { strategies: [] }; // store our data in memory
  /* readonly */ strategies$ = this._strategies$.asObservable();

  /* private */ _bankRate$ = new BehaviorSubject(null);
  /* readonly */ bankRate$ = this._bankRate$.asObservable();

  /* private */ _assetBreakdown$ = new BehaviorSubject(null);
  /* readonly */ assetBreakdown$ = this._assetBreakdown$.asObservable();

  /* private */ _portfolioAllocationsTable$ = new BehaviorSubject(null);
  /* readonly */ portfolioAllocationsTable$ = this._portfolioAllocationsTable$.asObservable();

  /* private */ _portfolioSummary$ = new BehaviorSubject(null);
  /* readonly */ portfolioSummary$ = this._portfolioSummary$.asObservable();

  /* private */ _historicalReturns$ = new BehaviorSubject(null);
  /* readonly */ historicalReturns$ = this._historicalReturns$.asObservable();

  /* private */ _exploreOtherOptions$ = new BehaviorSubject(null);
  /* readonly */ exploreOtherOptions$ = this._exploreOtherOptions$.asObservable();

  /* private */ _isLoading$ = new BehaviorSubject(null);
  /* readonly */ isLoading$ = this._isLoading$.asObservable();

  /**
  * Main subject used to fire off changes to update
  * data shown on recommendation page.
  */
  _goalSession$ = new BehaviorSubject(null);

  /**
   * Cache all possible result sets
   * for the recommendation page.
   * There are 32 possibilities.
  */
  questionnaireDataSourceMap = new Map();

  /**
   * Cache results of getHistoricalReturns function.
   * Key: StrategyId
   * The key is strategyId only because the portfolio tier has no impact
   * on the data set returned.
   * 8 possible data sets
   */
  getHistoricalReturnsMap: Map = new Map();

  // TODO: Hardcoded
  // src\client\app\shared\models\Enum\StrategyType.js
  toggleStrategyMap = new Map([
    [5, 8],
    [6, 9],
    [7, 10],
    [11, 12],
    [8, 5],
    [9, 6],
    [10, 7],
    [12, 11]
  ]);

  // TODO: Hardcoded
  // Headers for recommendation page
  headers = new Map([
    ['retirement', {
      title: 'Your Retirement Portfolio',
      text: 'Saving for the future takes a customized portfolio.'
    }],
    ['purchase', {
      title: 'Your Purchase Savings Portfolio',
      text: 'Saving for a major purchase takes a customized portfolio.'
    }],
    ['income', {
      title: 'Your Income-Building Portfolio',
      text: 'It takes a customized portfolio to generate income from investing.'
    }],
    ['wealth', {
      title: 'Your Wealth-Building Portfolio',
      text: 'Building wealth from your investments takes a customized portfolio.'
    }]
  ]);

  /* private */ _recommendationHeaders$ = new BehaviorSubject(this.headers.get('retirement'));
  /* readonly */ recommendationHeaders$ = this._recommendationHeaders$.asObservable();

  constructor(flowManager: FlowManagerService,
    appActions: ApplicationActions,
    appProxy: ApplicationProxyService) {
    this.flowManager = flowManager;
    this.appProxy = appProxy;
    this.appActions = appActions;

    this._goalSession$.pipe(
      distinctUntilChanged((a, b) => _.isEqual(a, b)),
      filter(x => x !== null)).subscribe(next => {

        // UI/UX wants different loading if you came from questionnaire vs make changes modal
        if (this.flowManager.goalSession.useInitialLoader === true) {
          this._isLoading$.next('loading');
          this.flowManager.goalSession = { ...this.flowManager.goalSession, useInitialLoader: false };
        } else {
          this._isLoading$.next(true);
        }

        try {
          // isDirty existing on goalSession indicates the next time a user needs to load
          // portfolio data that we should immediatly make a recommendation and set it.
          // isDirty is set to false afterwards
          // Better name? needsRecommendation
          if ((next.isDirty || next.isDirty == null)) {
            this.flowManager.goalSession = { ...this.flowManager.goalSession, isDirty: false };
            this.getRecommendedPortfolio();
          } else {
            const strategyId = StrategyType[next.strategy];
            const portfolioTierId = next.userPortfolioSelection || next.portfolioTier;
            this.loadStrategy(strategyId, +portfolioTierId);
          }
        } catch (e) {
          // console.log('catch block');
        }
      }, error => {
        console.log(error);
      });
  }

  /**
  * Returns a strategy's historical values for all underlying portfolios
  * numYears determines how many years to go back.
  * For example: If latest data available is 2020 and numYears is 5 then
  * data for 2016-2020 should be returned.
  * @param {number} strategyId
  * @param {number} numYears
  */
  getHistoricValues(strategyId: number, numYears: number) {
    return this.appProxy.getHistoricValues({ strategies: strategyId, numYears });
  }

  /**
  * Returns ally savings account rate.
  * Has flexibility to take other rateTypes if we ever expand out
  * @param {number} rateTypeId
  */
  getBankRateByRateType(rateTypeId: number) {
    return this.appProxy.getBankRateByRateType({ rateTypeId });
  }

  /**
  * Returns all assets and holdings for one strategy
  * @param {number} strategyId
  */
  getPortfolioTierAssetsAndHoldings(strategyId: number) {
    return this.appProxy.getPortfolioTierAssetsAndHoldings([strategyId]);
  }

  get bankRate(): Observable<number> {
    // 1 is savings rate
    if (this._bankRate$.getValue() == null) {
      this.getBankRateByRateType(1).subscribe(x => this._bankRate$.next(x.result));
    }
    return this.bankRate$;
  }

  /**
  * Return model portfolio id based on risk assessment
  * @param {ModelSelectionRequest} : modelSelectionRequest
  */
  getModelSelection(modelSelectionRequest: ModelSelectionRequest) {
    return this.appProxy.getModelSelection(this.convertReduxModel(modelSelectionRequest));
  }

  /**
  * Format goal session data to be properly consumed by
  * model selection api
  * @param {ModelSelectionRequest} : modelSelectionRequest
  */
  convertReduxModel(modelSelectionRequest: ModelSelectionRequest) {
    return {
      investableAssets: modelSelectionRequest.liquidAssets,
      riskTolerance: RiskTolerance[modelSelectionRequest.riskTolerance],
      strategyId: StrategyType[modelSelectionRequest.strategy],
      modelSet: 'default',
      goal: {
        initialInvestment: modelSelectionRequest.initialInvestment,
        timeHorizon: TimeHorizon.find(x => x.key === modelSelectionRequest.timeHorizon).apiValue
      }
    };
  }

  /**
  * Notify goal session change that requires components to update.
  * Kicks off chain of events to get data.
  */
  updatePortfolio() {
    this._goalSession$.next(this.flowManager.goalSession);
  }

  /**
  * Executes model selection and initializes process to update recommendation page
  */
  getRecommendedPortfolio() {
    const modelSelectionRequest = this.flowManager.goalSession;

    this.getModelSelection(modelSelectionRequest).pipe(
      map(response => {
        if (response.status !== 0) {
          throw new Error(response.message);
        } else {
          return {
            portfolioTier: response.result.modelPortfolio.portfolioTier,
            riskScore: response.result.riskScore,
            riskToleranceScore: response.result.modelPortfolio.RiskToleranceScore,
            timeHorizonScore: response.result.modelPortfolio.TimeHorizonScore,
            wealthRatioScore: response.result.modelPortfolio.WealthRatioScore
          };
        }
      }),
      retryWhen(genericRetryStrategy()),
      catchError(error => of(error))
    ).subscribe((mappedModelSelectionResponse: MappedModelSelectionResponse) => {
      if (!mappedModelSelectionResponse.portfolioTier) {
        // todo: figure out a better way to handle this.
        throw new Error('Error missing portfolio tier from model selection response.');
      }

      const strategyId = StrategyType[modelSelectionRequest.strategy];

      this.flowManager.goalSession = {
        ...this.flowManager.goalSession,
        ...mappedModelSelectionResponse,
        strategyId,
        userPortfolioSelection: null,
        isUserSelectedPortfolio: null,
        isIncome: QuestionnaireTransformer.isIncome(strategyId)
      };

      this._recommendationHeaders$.next(this.headers.get(modelSelectionRequest.goal));

      return this.loadStrategy(strategyId, mappedModelSelectionResponse.portfolioTier);
    });
  }

  /**
  * Return true if strategy already exists in datastore
  * @param {number} strategyId
  */
  strategyExists(strategyId: number): boolean {
    return this.dataStore.strategies.findIndex(x => x.strategyId === strategyId) !== -1;
  }

  /**
  * Load/get strategy into datastore and update obs
  * If data already exists in datastore then just updates obs
  * @param {number} strategyId
  * @param {number} portfolioTierId
  */
  loadStrategy(strategyId: number, portfolioTierId: number) {
    // Check if strategy already exists in datastore
    if (this.strategyExists(strategyId)) {
      // Data is available
      this.updateRecommendation(strategyId, portfolioTierId);
      return;
    }
    // Data is not available. DM the server
    // Merge http requests into single response object
    forkJoin(
      this.getHistoricValues([strategyId], 5), // historicValues, portfolios
      this.getPortfolioTierAssetsAndHoldings(strategyId) // holdings, asset breakdown, table allocations
    ).pipe(
      map(([historicValues, assetSets]) => {
        if (!(assetSets.status === 0 && historicValues.status === 0)) {
          throw new Error('Error during portfolio data retreival.');
        } else {
          return {
            // Need to handle errors
            strategyId,
            portfolios:
              _.merge(QuestionnaireTransformer.mapHistoricalValues(historicValues.result.Result),
                QuestionnaireTransformer.mapAssetSets(assetSets.result.Result)),
            isError: !(assetSets.status === 0 && historicValues.status === 0)
          };
        }
      }),
      retryWhen(genericRetryStrategy()),
      catchError(error => of(error))
    ).subscribe(x => {
        // TODO: Error handling in general
      if (x.isError) {
        throw new Error('Error retrieving portfolio data.');
      }
        // Check if it already exists before pushing
      if (this.dataStore.strategies.findIndex(x => x.strategyId === strategyId) !== -1) {
        this.updateRecommendation(strategyId, portfolioTierId);
        return;
      }

      this.dataStore.strategies.push(x);

        // Update datastore
      this._strategies$.next(Object.assign({}, this.dataStore));

        // Data is available
        // Update all streams
      this.updateRecommendation(strategyId, portfolioTierId);
    }, error => {
      console.error('Error during strategy load:', error);
    });
  }

  /**
  * Return a strategy's historical returns.
  * Used by historical returns modal
  * @param {Strategy} strategy
  * @param {number} portfolioTierId
  */
  getHistoricalReturns(strategy: Strategy, key: number) {
    if (!this.getHistoricalReturnsMap.has(key)) {
      this.getHistoricalReturnsMap.set(key,
        QuestionnaireTransformer.historicalReturns(strategy));
    }
    this._historicalReturns$.next(this.getHistoricalReturnsMap.get(key));
  }

  /**
  * Update the observables for recommendation
  * @param {number} strategyId
  * @param {number} portfolioTierId
  */
  updateRecommendation(strategyId: number, portfolioTierId: number) {
    this.strategies$.pipe(
      filter(x => x.strategies.length !== 0),
      map(x => (x.strategies.find(y => y.strategyId === strategyId))))
      .subscribe(strategy => {
        const portfolioKey: PortfolioKey = {
          strategy,
          portfolioTierId,
          recommendedPortfolioTierId: this.flowManager.goalSession.portfolioTier
        };
        this.updateObservables(portfolioKey);
      }).unsubscribe();
  }

  finishLoading() {
    this._isLoading$.next(false);
  }

  /**
  * Updates the questionnaireDataSourceMap.
  * This is the main method to use for modifying it.
  * @param {string} key
  * @param {string} propertyName
  * @param {any} data
  */
  updateQuestionnaireDataSourceMap(key: string, propertyName: string, data: any) {
    this.questionnaireDataSourceMap.set(key,
      Object.assign(
        this.questionnaireDataSourceMap.get(key),
        { [propertyName]: data }
      ));
  }

  /**
  * Starts the process of updating the questionnaireDataSource cache
  * with the data needed for each component. It creates a key based on
  * the combination of strategy and portfolioTier. If that key already
  * exists then we can bypass creating the objects and get them straight
  * from the map.
  * @param {Strategy} strategy
  * @param {number} portfolioTierId
  */
  updateObservables(portfolioKey: PortfolioKey) {
    const cacheKey = `${portfolioKey.strategy.strategyId}|${portfolioKey.portfolioTierId}`;

    if (!this.questionnaireDataSourceMap.has(cacheKey)) {
      // This key does not exist so set up an empty object to start updating
      this.questionnaireDataSourceMap.set(cacheKey, {});

      QuestionnaireTransformer.questionnaireUpdateFunctions.forEach(func => {
        this.updateQuestionnaireDataSourceMap(cacheKey, func.key,
          func.function(portfolioKey)
        );
      });
    }
    this.notifyGets(this.questionnaireDataSourceMap.get(cacheKey));

    // Historical returns only
    // Only needs strategy and has its own cache function because the data is
    // specific to strategy and does not care about portfolioTierId
    this.getHistoricalReturns(portfolioKey.strategy, +portfolioKey.strategy.strategyId);

    // Only provided to make it easier for QA
    this.getModelPortfolioId(portfolioKey.strategy, portfolioKey.portfolioTierId);

    // End loading animation
    this.finishLoading();
  }

  /**
   * Notifies all subjects to push data.
   * Components that are subscribed are then updated.
   * @param {any} data
   */
  notifyGets(data: any) {
    this._portfolioSummary$.next(data.getPortfolioSummary);
    this._portfolioAllocationsTable$.next(data.getTableAllocations);
    this._assetBreakdown$.next(data.getAssetBreakdown);
    this._exploreOtherOptions$.next(data.getExploreOtherOptions);
  }

  // For QA to verify
  getModelPortfolioId(strategy: Strategy, portfolioTierId: number) {
    this.flowManager.goalSession = {
      ...this.flowManager.goalSession,
      modelPortfolioId: (strategy.portfolios.find(x => x.portfolioTierId === portfolioTierId).modelPortfolioId)
    };
  }

  /**
  * Switch between free and paid version of strategy
  * @param {StrategyType} strategy
  */
  toggleStrategy(strategy: StrategyType) {
    const strategyId = this.toggleStrategyMap.get(StrategyType[strategy]);
    return Object.entries(StrategyType).find(x => x[1] === strategyId)[0];
  }
}
